import { capturedAtFilter } from "../analytics-window";
import type { AnalyticsQueryPayload } from "../api/analytics";
import { topicScopedResponseCountMetric } from "../api/analytics-metrics";
import { dataFileFilter } from "../api/analytics-rows";

export type TopicSentimentBand = "negative" | "positive";

export type BuildTopicSentimentBandQueryOptions = {
  topicRecordId: string;
  rollupRecordIds: string[];
  dateWindow?: { from?: string; to?: string };
  dataFileIds?: string[];
  sentimentAggregationFn: string;
  sentimentBand: TopicSentimentBand;
  includeSummary?: boolean;
  includeSourceIds?: boolean;
  limit?: number;
};

export function buildTopicSentimentBandQuery(
  options: BuildTopicSentimentBandQueryOptions,
): AnalyticsQueryPayload {
  const recordIds = normaliseRollupRecordIds(options.rollupRecordIds, options.topicRecordId);

  return {
    dimension_type: "topic",
    group_by: [],
    metrics: [
      topicScopedResponseCountMetric(),
      {
        name: "aggregate_sentiment",
        fn: options.sentimentAggregationFn,
        field: "dimension_numeric",
      },
    ],
    filters: [
      { field: "dimension_record_id", operator: "in", values: recordIds },
      sentimentBandFilter(options.sentimentBand),
      ...capturedAtFilter(options.dateWindow ?? {}),
      ...dataFileFilter(options.dataFileIds ?? []),
    ],
    limit: options.limit ?? 1,
    ...(options.includeSourceIds === true ? { include_source_ids: true } : {}),
    ...(options.includeSummary === true ? { include_summary: true } : {}),
  };
}

function sentimentBandFilter(sentimentBand: TopicSentimentBand) {
  if (sentimentBand === "negative") {
    return { field: "dimension_numeric", operator: "lt" as const, value: 0 };
  }
  return { field: "dimension_numeric", operator: "gte" as const, value: 0 };
}

function normaliseRollupRecordIds(
  rollupRecordIds: string[],
  topicRecordId: string,
): string[] {
  const topicId = topicRecordId.trim();
  const ids = rollupRecordIds
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
  if (ids.length === 0) {
    throw new Error("Topic sentiment band query requires at least one rollup record ID.");
  }
  if (!topicId || !ids.includes(topicId)) {
    throw new Error("Topic sentiment band rollup must include the selected topic record ID.");
  }
  return ids;
}
